import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Database,
  Filter,
  Loader2,
  Pencil,
  Plus,
  PlugZap,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Trash2,
  X,
  XCircle,
  ServerCrash,
  Lock,
} from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { storage } from '../../../Shared/Utils/authStorage';
import { deleteDataConnection, getDataConnections, testStoredConnection } from '../../Services/DataService';
import {
  CONNECTION_STATUS_STYLES,
  ENVIRONMENT_OPTIONS,
  ENVIRONMENT_STYLES,
  DataConnection,
  DataConnectionStats,
  ENGINE_OPTIONS,
  TEST_STATUS_STYLES,
} from '../../Types/DataServiceType';
import ConnectionFormModal from '../DataService/ConnectionFormModal';

const PAGE_SIZE = 8;

const emptyStats: DataConnectionStats = {
  total: 0,
  development: 0,
  staging: 0,
  production: 0,
  active: 0,
  disabled: 0,
  error: 0,
  healthy: 0,
  failing: 0,
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center justify-between">
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${accent}`}>{icon}</span>
      <span className="text-2xl font-black text-slate-100">{value}</span>
    </div>
    <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
  </div>
);

const ConnectionsComponent = () => {
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<DataConnection> | null>(null);
  const [stats, setStats] = useState<DataConnectionStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [engine, setEngine] = useState('');
  const [environment, setEnvironment] = useState('');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DataConnection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DataConnection | null>(null);

  // Creating, editing, deleting and testing a connection are all super-admin
  // operations on the server, so a delegate gets a read-only catalogue.
  const isSuperAdmin = (storage.getRole() || '').toUpperCase() === 'SUPER_ADMIN';
  const [deleting, setDeleting] = useState(false);
  const [testingId, setTestingId] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getDataConnections(
        {
          search: debouncedSearch || undefined,
          engine: engine || undefined,
          environment: environment || undefined,
        },
        page,
        PAGE_SIZE
      );

      if (response.success && response.data) {
        const pageData = response.data.page;
        setConnections(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setConnections([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load connections.');
      }
    } catch (err) {
      console.error('Failed to load connections', err);
      setConnections([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading connections.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, engine, environment, page]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, engine, environment]);

  const hasActiveFilters = Boolean(search) || Boolean(engine) || Boolean(environment);

  const resetFilters = () => {
    setSearch('');
    setEngine('');
    setEnvironment('');
  };

  const handleTest = async (connection: DataConnection) => {
    setTestingId(connection.id);
    try {
      const response = await testStoredConnection(connection.id);
      if (response.success && response.data) {
        const ok = response.data.status === 'OK';
        setAlert({
          variant: ok ? 'success' : 'warning',
          title: ok ? 'Connection healthy' : `Test: ${response.data.status}`,
          message: response.data.message,
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      } else {
        setAlert({
          variant: 'error',
          title: 'Test failed',
          message: response.message || 'The connection test could not be run.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
      fetchConnections();
    } finally {
      setTestingId('');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteDataConnection(deleteTarget.id);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Deleted',
          message: `"${deleteTarget.alias}" was removed along with its grants and saved queries.`,
          autoCloseMs: 4000,
          onClose: () => setAlert(null),
        });
        setDeleteTarget(null);
        fetchConnections();
      } else {
        setAlert({
          variant: 'error',
          title: 'Delete failed',
          message: response.message || 'The connection could not be removed.',
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Failed to delete connection', err);
      setAlert({
        variant: 'error',
        title: 'System error',
        message: 'Something went wrong. Please try again.',
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} />}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Database size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Connections</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Registered databases available to the data service
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchConnections}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
              className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400"
            >
              <Plus size={15} /> New
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Total"
          value={stats.total}
          icon={<Database size={16} />}
          accent="border-cyan-500/30 bg-cyan-500/10 text-cyan-400"
        />
        <StatCard
          label="Development"
          value={stats.development}
          icon={<Server size={16} />}
          accent="border-sky-500/30 bg-sky-500/10 text-sky-400"
        />
        <StatCard
          label="Production"
          value={stats.production}
          icon={<ServerCrash size={16} />}
          accent="border-rose-500/30 bg-rose-500/10 text-rose-400"
        />
        <StatCard
          label="Failing"
          value={stats.failing}
          icon={<XCircle size={16} />}
          accent="border-amber-500/30 bg-amber-500/10 text-amber-400"
        />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
          <Filter size={14} /> Filters
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search alias, database or host..."
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-9 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={15} />
              </button>
            )}
          </div>

          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
          >
            <option value="" className="bg-slate-900 text-slate-100">
              All engines
            </option>
            {ENGINE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
          >
            <option value="" className="bg-slate-900 text-slate-100">
              All environments
            </option>
            {ENVIRONMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} className="bg-slate-900 text-slate-100">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="mt-3 flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-rose-400 hover:border-rose-500/40"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1].map((idx) => (
            <div key={idx} className="animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
              <div className="mb-4 h-5 w-48 rounded bg-slate-800" />
              <div className="space-y-3">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-4 w-full rounded bg-slate-800/70" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : connections.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <Database size={26} className="mx-auto mb-2 text-slate-600" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            {isSuperAdmin ? 'No connections found' : 'No data-service access yet'}
          </p>
          {!isSuperAdmin && hasActiveFilters ? (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          ) : !isSuperAdmin ? (
            <p className="mx-auto mt-3 max-w-sm text-xs text-slate-500">
              Nobody has configured a data connection for you yet. An admin grants this, and a recruiter can
              share access with their own team.
            </p>
          ) : hasActiveFilters ? (
            <button onClick={resetFilters} className="mt-3 text-xs font-bold text-cyan-400 hover:underline">
              Clear filters
            </button>
          ) : (
            <button
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
              className="mt-3 text-xs font-bold text-cyan-400 hover:underline"
            >
              Register the first connection
            </button>
          )}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="space-y-3">
            {connections.map((connection) => (
              <div
                key={connection.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 transition hover:border-slate-700"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-black tracking-tight text-slate-100">{connection.alias}</h3>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                          ENVIRONMENT_STYLES[connection.environment] ??
                          'border-slate-700 bg-slate-950 text-slate-400'
                        }`}
                      >
                        {connection.environment}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-widest ${
                          CONNECTION_STATUS_STYLES[connection.status] ??
                          'border-slate-700 bg-slate-950 text-slate-400'
                        }`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {connection.status}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-slate-400">
                        <ShieldCheck size={11} />
                        {connection.effectivePermission === 'READ_WRITE' ? 'Read / write' : 'Read only'}
                      </span>
                      {connection.allowConsole && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-violet-400">
                          Console
                        </span>
                      )}
                    </div>

                    <p className="mt-2 truncate text-xs font-semibold text-slate-400">
                      {connection.username}@{connection.host}:{connection.port} / {connection.databaseName}
                      {connection.schemaName ? ` · ${connection.schemaName}` : ''}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px] font-bold text-slate-500">
                      <span className="uppercase tracking-widest">{connection.engine}</span>
                      <span>Row cap {connection.rowCap.toLocaleString()}</span>
                      <span>Timeout {connection.queryTimeoutSec}s</span>
                      <span className="flex items-center gap-1.5">
                        Last test:{' '}
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                            TEST_STATUS_STYLES[connection.lastTestStatus ?? 'NEVER'] ??
                            'border-slate-700 bg-slate-950 text-slate-400'
                          }`}
                        >
                          {connection.lastTestStatus ?? 'never'}
                        </span>{' '}
                        {formatDate(connection.lastTestedAt)}
                      </span>
                      {!connection.engineSupported && (
                        <span className="text-amber-400">
                          Driver not on the classpath for {connection.engine}
                        </span>
                      )}
                    </div>

                    {connection.lastTestMessage && connection.lastTestStatus !== 'OK' && (
                      <p className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold text-amber-400">
                        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                        {connection.lastTestMessage}
                      </p>
                    )}
                  </div>

                  {isSuperAdmin ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => handleTest(connection)}
                        disabled={testingId === connection.id}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
                      >
                        {testingId === connection.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <PlugZap size={12} />
                        )}
                        Test
                      </button>
                      <button
                        onClick={() => {
                          setEditing(connection);
                          setModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-slate-300 transition hover:border-cyan-500/40"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => setDeleteTarget(connection)}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 transition hover:border-rose-500/40"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  ) : (
                    // A delegate only ever gets read access to the catalogue.
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5 text-[11px] font-black uppercase tracking-widest text-slate-500">
                      <Lock size={12} /> Read only
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.totalElements > 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
              <Pagination
                page={pagination.page}
                size={pagination.size}
                totalElements={pagination.totalElements}
                totalPages={pagination.totalPages}
                first={pagination.first}
                last={pagination.last}
                hasNext={pagination.hasNext}
                hasPrevious={pagination.hasPrevious}
                setPage={setPage}
              />
            </div>
          )}
        </motion.div>
      )}

      <ConnectionFormModal
        isOpen={modalOpen}
        connection={editing}
        onClose={() => setModalOpen(false)}
        onSaved={(message) => {
          setAlert({ variant: 'success', title: 'Saved', message, autoCloseMs: 4000, onClose: () => setAlert(null) });
          fetchConnections();
        }}
      />

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">Delete connection?</h3>
                <p className="mt-1 text-sm text-slate-400">
                  <span className="font-bold text-slate-200">{deleteTarget.alias}</span> will be removed
                  along with every grant and saved query that references it. This cannot be undone.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 rounded-xl bg-rose-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-rose-400 disabled:opacity-50"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {deleting ? 'Deleting' : 'Delete'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsComponent;
